import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui";

/** Read-only value with a copy button — used for tokens shown exactly once. */
export function CopyField({ value, label }: { value: string; label: string }) {
  const { t } = useTranslation("common");
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context or denied) — the value stays selectable.
    }
  }

  return (
    /*
      Stacked below the width where a value stops fitting beside the button.
      The field carries `pointer-coarse:text-base`, which is right for tapping
      and makes the text wider exactly where there is least room — so on a
      phone the button was taking a third of the row and clipping the value it
      copies. An API key or a connection address is often read as well as
      copied, and neither can be read at half its length.
    */
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <input
        readOnly
        aria-label={label}
        value={value}
        onFocus={(event) => event.currentTarget.select()}
        /* `pointer-coarse:text-base` for the same reason the shared control
           carries it: this field selects its contents on focus, so tapping to
           copy a connection string is exactly the gesture that would leave iOS
           zoomed in on it. */
        className="w-full rounded-md border border-line-strong bg-canvas px-3 py-2 font-mono text-xs pointer-coarse:text-base text-fg"
      />
      <Button
        variant="secondary"
        size="sm"
        onClick={() => void copy()}
        className="shrink-0 self-start sm:self-auto"
      >
        {copied ? t("copy.copied") : t("copy.copy")}
      </Button>
    </div>
  );
}
